import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FiTrendingUp, FiChevronDown, FiX } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine, CartesianGrid } from 'recharts';
import { DisclosureCard, HelpIcon, useLanguage } from '../components';
import { downloadCsv } from '../utils/exportCsv';
import './PerformanceDashboard.css';

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatPct = (value, digits = 2) => {
  const num = asNumber(value);
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(digits)}%`;
};

const formatCurrency = (value) =>
  asNumber(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const extractComparisonMetrics = (portfolio, performance = null, tt = (v) => v) => {
  const data = portfolio?.data || {};
  const summary = data.summary || {};
  const metrics = data.metrics || {};
  const holdings = Array.isArray(data.holdings) ? data.holdings : [];
  const investment = asNumber(data.investment_amount, 0);

  const expectedReturn = asNumber(summary.expected_return ?? metrics.expected_return);
  const volatility = asNumber(summary.volatility ?? metrics.volatility);
  const sharpe = asNumber(metrics.sharpe_ratio ?? summary.sharpe_ratio);
  const holdingsCurrentValue = holdings.reduce((sum, holding) => {
    const holdingValue = asNumber(holding?.value);
    if (holdingValue > 0) return sum + holdingValue;
    return sum + (investment * asNumber(holding?.weight)) / 100;
  }, 0);
  const currentValue = asNumber(
    performance?.current_actual_value,
    holdingsCurrentValue > 0 ? holdingsCurrentValue : investment,
  );
  const gainLossPct = performance?.total_return_pct != null
    ? asNumber(performance.total_return_pct)
    : (investment > 0 ? ((currentValue - investment) / investment) * 100 : 0);
  const modelReturnPct = performance?.projected_return_pct != null
    ? asNumber(performance.projected_return_pct)
    : (Math.abs(expectedReturn) > 1.5 ? expectedReturn : expectedReturn * 100);

  const sectorMap = {};
  holdings.forEach((holding) => {
    const sector = holding.sector || tt('Unknown');
    sectorMap[sector] = (sectorMap[sector] || 0) + asNumber(holding.weight);
  });

  const topSectors = Object.entries(sectorMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const topHoldings = [...holdings]
    .sort((a, b) => asNumber(b.weight) - asNumber(a.weight))
    .slice(0, 5);

  return {
    name: portfolio.name || tt('Unnamed'),
    source: portfolio.source || tt('manual'),
    createdAt: portfolio.created_at,
    holdingsCount: holdings.length,
    investment,
    currentValue,
    gainLossPct,
    modelReturnPct,
    expectedReturn: Math.abs(expectedReturn) > 1.5 ? expectedReturn : expectedReturn * 100,
    volatility: Math.abs(volatility) > 1.5 ? volatility : volatility * 100,
    sharpe,
    topSectors,
    topHoldings,
  };
};

export default function PerformanceDashboard({ apiBase }) {
  const { tt } = useLanguage();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [perfLoading, setPerfLoading] = useState(false);
  const [error, setError] = useState('');
  const [comparisonSelected, setComparisonSelected] = useState([]);
  const [comparisonPerformanceById, setComparisonPerformanceById] = useState({});

  const comparisonMetricRows = useMemo(() => ([
    {
      key: 'createdAt',
      label: tt('Date Created'),
      format: (v) => (v ? new Date(v).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : tt('N/A')),
    },
    { key: 'holdingsCount', label: tt('Holdings'), format: (v) => v },
    { key: 'investment', label: tt('Investment'), format: formatCurrency },
    {
      key: 'currentValue',
      label: tt('Current Value'),
      format: formatCurrency,
      help: tt('Latest actual value based on tracked portfolio performance and market prices.'),
    },
    {
      key: 'gainLossPct',
      label: tt('Current Gain/Loss'),
      format: (v) => formatPct(v, 1),
      help: tt('Actual gain or loss since the portfolio was created.'),
    },
    {
      key: 'modelReturnPct',
      label: tt('Model Estimate'),
      format: (v) => formatPct(v, 1),
      help: tt('Model-projected return over the same period using factor-based expected returns.'),
    },
    {
      key: 'expectedReturn',
      label: tt('Expected Return'),
      format: (v) => formatPct(v),
      help: tt('Annualized expected return from the FF5 factor model.'),
    },
    {
      key: 'volatility',
      label: tt('Volatility'),
      format: (v) => formatPct(v, 1),
      help: tt('Annualized standard deviation of portfolio returns.'),
    },
    {
      key: 'sharpe',
      label: tt('Sharpe Ratio'),
      format: (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : tt('N/A')),
      help: tt('Risk-adjusted return: (Return − Rf) ÷ Volatility.'),
    },
    { key: 'source', label: tt('Source'), format: (v) => v },
  ]), [tt]);

  const fetchPortfolios = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/portfolios`);
      const list = res.data.portfolios || [];
      setPortfolios(list);
      if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
    } catch (err) {
      if (err.response?.status === 401) setError(tt('Please log in to view performance.'));
      else setError(tt('Failed to load portfolios.'));
    } finally {
      setLoading(false);
    }
  }, [apiBase, selectedId, tt]);

  useEffect(() => { fetchPortfolios(); }, [fetchPortfolios]);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    const fetchPerf = async () => {
      setPerfLoading(true);
      try {
        const res = await axios.get(`${apiBase}/portfolio-performance/${selectedId}`);
        if (!cancelled) setPerfData(res.data);
      } catch {
        if (!cancelled) setPerfData(null);
      } finally {
        if (!cancelled) setPerfLoading(false);
      }
    };
    fetchPerf();
    return () => { cancelled = true; };
  }, [apiBase, selectedId]);

  useEffect(() => {
    if (comparisonSelected.length === 0) return;

    let cancelled = false;
    const fetchComparisonPerformance = async () => {
      const responses = await Promise.all(comparisonSelected.map(async (id) => {
        try {
          const response = await axios.get(`${apiBase}/portfolio-performance/${id}`, { withCredentials: true });
          return [id, response.data];
        } catch {
          return [id, null];
        }
      }));

      if (!cancelled) {
        setComparisonPerformanceById((prev) => {
          const next = { ...prev };
          responses.forEach(([id, data]) => {
            if (data) next[id] = data;
          });
          return next;
        });
      }
    };

    fetchComparisonPerformance();
    return () => { cancelled = true; };
  }, [apiBase, comparisonSelected]);

  const fmt = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  // Compute Y-axis domain that zooms into the actual data range
  const yDomain = useMemo(() => {
    if (!perfData?.series?.length) return [0, 100];
    let min = Infinity, max = -Infinity;
    for (const pt of perfData.series) {
      const a = pt.actual ?? Infinity;
      const p = pt.projected ?? Infinity;
      if (a < min) min = a;
      if (p < min) min = p;
      if (a > max) max = a;
      if (p > max) max = p;
    }
    // Also include the investment amount in the range
    const inv = perfData.investment_amount || 0;
    if (inv < min) min = inv;
    if (inv > max) max = inv;
    const padding = Math.max((max - min) * 0.15, max * 0.01);
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [perfData]);

  // Smart Y-axis tick formatter based on range
  const formatYAxis = useCallback((v) => {
    const range = yDomain[1] - yDomain[0];
    if (range < 2000) return `$${Number(v).toLocaleString()}`;
    if (range < 20000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${(v / 1000).toFixed(0)}k`;
  }, [yDomain]);

  // Gain/loss since inception
  const gainLoss = perfData ? perfData.current_actual_value - perfData.investment_amount : 0;
  const comparedPortfolios = useMemo(
    () => portfolios
      .filter((portfolio) => comparisonSelected.includes(portfolio.id))
      .map((portfolio) => extractComparisonMetrics(portfolio, comparisonPerformanceById[portfolio.id], tt)),
    [comparisonPerformanceById, comparisonSelected, portfolios, tt],
  );

  const toggleComparisonSelect = useCallback((id) => {
    setComparisonSelected((prev) => (
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    ));
  }, []);

  const bestForMetric = useCallback((key, higherIsBetter = true) => {
    if (comparedPortfolios.length < 2) return -1;
    let bestIdx = 0;
    comparedPortfolios.forEach((portfolio, index) => {
      const current = asNumber(portfolio[key]);
      const best = asNumber(comparedPortfolios[bestIdx][key]);
      if (higherIsBetter ? current > best : current < best) bestIdx = index;
    });
    return bestIdx;
  }, [comparedPortfolios]);

  const handleExportComparison = useCallback(() => {
    if (comparedPortfolios.length === 0) return;
    const headers = ['Metric', ...comparedPortfolios.map((portfolio) => portfolio.name)];
    const rows = comparisonMetricRows.map((row) => ([
      row.label,
      ...comparedPortfolios.map((portfolio) => String(row.format(portfolio[row.key]))),
    ]));
    downloadCsv('portfolio_analytics_comparison', headers, rows);
  }, [comparisonMetricRows, comparedPortfolios]);

  if (loading) return <div className="perf-page"><div className="perf-loading">{tt('Loading portfolios...')}</div></div>;
  if (error) return <div className="perf-page"><div className="perf-error">{error}</div></div>;
  if (portfolios.length === 0) return (
    <div className="perf-page">
      <div className="perf-empty"> 
        <FiTrendingUp size={48} />
        <p>{tt('No saved portfolios yet.')}</p>
        <p>{tt('Save a portfolio from the Optimizer or Builder to track its performance.')}</p>
      </div>
    </div>
  );

  return (
    <div className="perf-page">
      <div className="perf-header">
        <h2><FiTrendingUp /> {tt('Portfolio Analytics')}</h2>
        <div className="perf-selector">
          <FiChevronDown className="perf-selector-icon" />
          <select value={selectedId || ''} onChange={e => setSelectedId(parseInt(e.target.value))}>
            {portfolios.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {perfLoading && <div className="perf-loading">{tt('Loading performance data...')}</div>}

      {perfData && !perfLoading && (
        <>
          <div className="perf-stats">
            <div className="perf-stat-card">
              <div className="perf-stat-label">{tt('Invested')}</div>
              <div className="perf-stat-value">{fmt(perfData.investment_amount)}</div>
            </div>
            <div className={`perf-stat-card ${perfData.total_return_pct >= 0 ? 'positive' : 'negative'}`}> 
              <div className="perf-stat-label">{tt('Current Value')}</div>
              <div className="perf-stat-value">{fmt(perfData.current_actual_value)}</div>
              <div className={`perf-stat-delta ${gainLoss >= 0 ? 'up' : 'down'}`}>
                {gainLoss >= 0 ? '+' : ''}{fmt(gainLoss)}
              </div>
            </div>
            <div className={`perf-stat-card ${perfData.total_return_pct >= 0 ? 'positive' : 'negative'}`}>
              <div className="perf-stat-label">{tt('Actual Return')}</div>
              <div className="perf-stat-value">{perfData.total_return_pct >= 0 ? '+' : ''}{perfData.total_return_pct.toFixed(2)}%</div>
            </div>
            <div className="perf-stat-card"> 
              <div className="perf-stat-label">{tt('Model Estimate')}</div>
              <div className="perf-stat-value">{perfData.projected_return_pct >= 0 ? '+' : ''}{perfData.projected_return_pct.toFixed(2)}%</div>
              <div className="perf-stat-note">{tt('FF5 projection')}</div>
            </div>
            <div className={`perf-stat-card ${perfData.alpha_pct >= 0 ? 'positive' : 'negative'}`}>
              <div className="perf-stat-label">{tt('Alpha')}</div>
              <div className="perf-stat-value">{perfData.alpha_pct >= 0 ? '+' : ''}{perfData.alpha_pct.toFixed(2)}%</div>
            </div>
          </div>

          <div className="perf-chart-card">
            <h3>{tt('Actual vs Model Projection')}</h3>
            <p className="perf-chart-sub">{perfData.days_tracked} {tt('days tracked since')} {perfData.created_date}</p>
            <p className="perf-chart-note">
              {tt('The dashed line shows the theoretical growth path based on expected returns from the factor model, not a guarantee.')} 
              {tt("The solid line shows your portfolio's real market performance.")}
            </p>
            <ResponsiveContainer width="100%" height={380}>
              <LineChart data={perfData.series} margin={{ top: 10, right: 20, left: 15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.15)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={40} tickFormatter={d => { try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return d; } }} />
                <YAxis
                  domain={yDomain}
                  tick={{ fill: '#888', fontSize: 11 }}
                  tickFormatter={formatYAxis}
                  width={70}
                />
                <ReferenceLine
                  y={perfData.investment_amount}
                  stroke="rgba(148,163,184,0.5)"
                  strokeDasharray="4 4"
                  label={{ value: tt('Invested'), fill: '#64748b', fontSize: 10, position: 'insideTopRight' }}
                />
                <Tooltip
                  contentStyle={{ background: '#1e2330', border: '1px solid #333', borderRadius: 8 }}
                  labelStyle={{ color: '#b0bec5' }}
                  formatter={(v, name) => [fmt(v), name]}
                />
                <Legend />
                <Line type="monotone" dataKey="actual" name={tt('Actual')} stroke="#00bcd4" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="projected" name={tt('Model Estimate')} stroke="#ff9800" strokeWidth={1.5} strokeDasharray="6 3" dot={false} opacity={0.7} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <DisclosureCard
        title={tt('Portfolio Comparison')}
        subtitle={tt('Select saved portfolios to compare them side by side.')}
        alwaysOpen
        className="perf-compare-disclosure"
      >
        <div className="perf-compare-selector">
          {portfolios.map((portfolio) => {
            const isSelected = comparisonSelected.includes(portfolio.id);
            return (
              <button
                key={portfolio.id}
                type="button"
                className={`perf-compare-chip ${isSelected ? 'active' : ''}`}
                onClick={() => toggleComparisonSelect(portfolio.id)}
              >
                {isSelected ? <FiX size={14} /> : null}
                {portfolio.name || tt('Unnamed')}
              </button>
            );
          })}
        </div>

        {comparedPortfolios.length >= 2 && (
          <>
            <div className="perf-compare-actions">
              <button type="button" className="perf-compare-export-btn" onClick={handleExportComparison}>
                {tt('Export Comparison CSV')}
              </button>
            </div>
            <div className="perf-compare-table-wrap">
              <table className="perf-compare-table">
                <thead>
                  <tr>
                    <th className="perf-compare-label-col">{tt('Metric')}</th>
                    {comparedPortfolios.map((portfolio) => (
                      <th key={portfolio.name}>
                        <div className="perf-compare-col-head">
                          <strong>{portfolio.name}</strong>
                          <small>{portfolio.source}</small>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonMetricRows.map((row) => {
                    const bestIdx = ['gainLossPct', 'modelReturnPct', 'expectedReturn', 'sharpe'].includes(row.key)
                      ? bestForMetric(row.key, true)
                      : row.key === 'volatility'
                      ? bestForMetric(row.key, false)
                      : -1;
                    return (
                      <tr key={row.key}>
                        <td className="perf-compare-label-col">
                          {row.label}
                          {row.help && <> <HelpIcon text={row.help} /></>}
                        </td>
                        {comparedPortfolios.map((portfolio, index) => (
                          <td key={portfolio.name} className={index === bestIdx ? 'perf-compare-best' : ''}>
                            {row.format(portfolio[row.key])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr>
                    <td className="perf-compare-label-col">{tt('Top Sectors')}</td>
                    {comparedPortfolios.map((portfolio) => (
                      <td key={portfolio.name}>
                        {portfolio.topSectors.map(([name, weight]) => (
                          <div key={name} className="perf-compare-sector-row">
                            <span>{name}</span>
                            <span>{weight.toFixed(1)}%</span>
                          </div>
                        ))}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="perf-compare-label-col">{tt('Top Holdings')}</td>
                    {comparedPortfolios.map((portfolio) => (
                      <td key={portfolio.name}>
                        {portfolio.topHoldings.map((holding) => (
                          <div key={holding.ticker} className="perf-compare-sector-row">
                            <span>{holding.ticker}</span>
                            <span>{asNumber(holding.weight).toFixed(1)}%</span>
                          </div>
                        ))}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {comparisonSelected.length === 1 && (
          <p className="perf-compare-hint">{tt('Select at least one more portfolio to compare.')}</p>
        )}

        {comparisonSelected.length === 0 && (
          <p className="perf-compare-hint">{tt('Select saved portfolios below to compare them side by side.')}</p>
        )}
      </DisclosureCard>
    </div>
  );
}
